import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { useNavigate } from 'react-router-dom';

const C = { teal:'#00B4D8', navy:'#0D1B2A', success:'#10B981', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA' };
const MASTERY_COLORS = { new:'#E5E7EB', learning:'#DBEAFE', learned:'#D1FAE5', mastered:'#FEF9C3' };
const MASTERY_TEXT = { new:C.grey, learning:'#1D4ED8', learned:'#065F46', mastered:'#713F12' };

const SAMPLE_WORDS = [
  { word:'diligent', definition:'having or showing care in one\'s work', example:'She is a diligent student.', mastery:'mastered', lesson_type:'vocabulary' },
  { word:'eloquent', definition:'fluent or persuasive in speaking', example:'He gave an eloquent speech.', mastery:'learned', lesson_type:'conversation' },
  { word:'perseverance', definition:'continued effort despite difficulty', example:'Her perseverance paid off.', mastery:'learning', lesson_type:'ielts' },
  { word:'ambiguous', definition:'open to more than one interpretation', example:'The instructions were ambiguous.', mastery:'new', lesson_type:'grammar' },
  { word:'collaborate', definition:'work jointly on an activity', example:'We collaborated on the project.', mastery:'learned', lesson_type:'business' },
  { word:'pragmatic', definition:'dealing with things sensibly', example:'A pragmatic approach is needed.', mastery:'mastered', lesson_type:'vocabulary' },
];

export default function VocabularyBank() {
  const [words, setWords] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/student/vocabulary')
      .then(r => setWords(r.data?.words || SAMPLE_WORDS))
      .catch(() => setWords(SAMPLE_WORDS))
      .finally(() => setLoading(false));
  }, []);

  const filtered = words.filter(w => {
    const matchSearch = w.word?.toLowerCase().includes(search.toLowerCase()) || w.definition?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter==='all' || w.mastery===filter;
    return matchSearch && matchFilter;
  });

  return (
    <div style={{ background:C.bg, minHeight:'100vh' }}>
      <header style={{ background:C.navy, color:'#fff', padding:'0 32px', height:64, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ fontWeight:800, fontSize:20 }}>SpeakIQ</div>
        <nav style={{ display:'flex', gap:24 }}>
          {[['Dashboard','/dashboard'],['Vocabulary','/vocabulary'],['Certificates','/my-certificates']].map(([l,p]) => (
            <button key={p} onClick={() => navigate(p)} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.8)', fontSize:14, fontWeight:500, cursor:'pointer' }}>{l}</button>
          ))}
        </nav>
      </header>

      <div style={{ maxWidth:1200, margin:'0 auto', padding:'32px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <h1 style={{ margin:0, fontSize:28, fontWeight:800, color:C.dark }}>Vocabulary Bank</h1>
          <div style={{ fontSize:24, fontWeight:800, color:C.teal }}>{words.length} <span style={{ fontSize:14, fontWeight:400, color:C.grey }}>words learned</span></div>
        </div>
        <div style={{ display:'flex', gap:8, marginBottom:24, flexWrap:'wrap' }}>
          {['all','new','learning','learned','mastered'].map(m => (
            <button key={m} onClick={() => setFilter(m)} style={{
              padding:'6px 16px', borderRadius:20, fontSize:13, fontWeight:600, cursor:'pointer',
              background: filter===m ? C.teal : '#fff',
              color: filter===m ? '#fff' : C.grey,
              border: `1px solid ${filter===m ? C.teal : C.border}`,
            }}>
              {m === 'all' ? 'All Words' : m.charAt(0).toUpperCase()+m.slice(1)} ({m==='all'?words.length:words.filter(w=>w.mastery===m).length})
            </button>
          ))}
        </div>
        <div style={{ position:'relative', marginBottom:24 }}>
          <MagnifyingGlassIcon style={{ width:16, height:16, position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:C.grey }} />
          <input placeholder="Search words or definitions..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ width:'100%', maxWidth:400, padding:'10px 12px 10px 36px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:14 }} />
        </div>
        {loading ? (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:16 }}>
            {[1,2,3,4,5,6].map(i => <div key={i} className="skeleton" style={{ height:140, borderRadius:12 }} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:'center', padding:64, color:C.grey }}>
            <BookOpenIcon style={{ width:48, height:48, margin:'0 auto 12px', color:'#D1D5DB' }} />
            <h3>No words found</h3>
            <p>Start lessons to build your vocabulary bank!</p>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:16 }}>
            {filtered.map((w, i) => (
              <div key={i} className="card" style={{ padding:20 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                  <div style={{ fontSize:20, fontWeight:800, color:C.dark }}>{w.word}</div>
                  <span className="badge" style={{ background: MASTERY_COLORS[w.mastery]||'#E5E7EB', color: MASTERY_TEXT[w.mastery]||C.grey }}>
                    {w.mastery}
                  </span>
                </div>
                <p style={{ margin:'0 0 8px', fontSize:13, color:C.grey, lineHeight:1.5 }}>{w.definition}</p>
                {w.example && (
                  <p style={{ margin:0, fontSize:12, color:'#9CA3AF', fontStyle:'italic', borderLeft:`3px solid ${C.border}`, paddingLeft:8, lineHeight:1.5 }}>
                    "{w.example}"
                  </p>
                )}
                <div style={{ marginTop:8, fontSize:11, color:C.grey }}>
                  Learned in: <span style={{ fontWeight:600, textTransform:'capitalize' }}>{w.lesson_type}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
